import React, { useState, useMemo, useCallback, useContext, createContext, ReactNode } from "react";

import { clearAuthToken, readAuthToken, writeAuthToken } from "../../utils/authToken";

/**
 * The reactive view of the session token. `utils/authToken` owns the cookie; this holds a copy in
 * state so that `useAuthRedirect` and `useApi` re-run when it changes — a cookie read on its own
 * never notifies anyone.
 *
 * This replaced a generic `CookieContext` that handed out the whole cookie jar. Every consumer
 * only ever wanted `cookies.token`, and typing the jar as `Record<string, string>` meant a
 * misspelled cookie name was an unchecked `undefined` rather than a compile error.
 */
export type AuthTokenContextValue = {
  /** `undefined` when there is no session. */
  token: string | undefined;
  setToken: (token: string, expiresAt?: Date) => void;
  clearToken: () => void;
};

// Only reached with no provider mounted, which is a wiring mistake rather than a state the app
// can be in — so it throws instead of pretending there is no session.
const missingProvider = (member: string) => () => {
  throw new Error(`${member} used outside AuthTokenProvider`);
};

const AuthTokenContext = createContext<AuthTokenContextValue>({
  token: undefined,
  setToken: missingProvider("setToken"),
  clearToken: missingProvider("clearToken"),
});

export const useAuthToken = () => useContext(AuthTokenContext);

const AuthTokenProvider = ({ children }: { children: ReactNode }) => {
  const [token, setTokenState] = useState(readAuthToken);

  // Memoised: `useApi` memoises on `setToken`/`clearToken` identity and the 401 listener
  // subscribes on `clearToken`, so a new identity per render would rebuild and resubscribe both.
  const setToken = useCallback((next: string, expiresAt?: Date) => {
    writeAuthToken(next, expiresAt);
    setTokenState(next);
  }, []);

  const clearToken = useCallback(() => {
    clearAuthToken();
    setTokenState(undefined);
  }, []);

  const value = useMemo(() => ({ token, setToken, clearToken }), [token, setToken, clearToken]);

  return <AuthTokenContext.Provider value={value}>{children}</AuthTokenContext.Provider>;
};

export default AuthTokenProvider;
